<c-container class="mt-5">
  <h2 data-testid="error-category-page-title">Error Category Management</h2>
  <p class="subtitle">Manage error categories, their types, and user-facing messages</p>

  @if (uiState === uiStateForTemplate.ShowLoading) {
    <mat-spinner data-testid="loading-spinner"></mat-spinner>
  } @else if (uiState === uiStateForTemplate.ShowData) {
    <div class="summary-bar" data-testid="summary-bar">
      <span class="summary-item" data-testid="category-count">
        <strong>{{ categories.length }}</strong> categories
      </span>
      <span class="summary-item" data-testid="error-type-count">
        <strong>{{ getTotalErrorTypes() }}</strong> error types
      </span>
    </div>

    <mat-form-field appearance="outline" class="search-field">
      <mat-label>Search categories, error types, or messages</mat-label>
      <input matInput
             [(ngModel)]="searchText"
             (ngModelChange)="onSearchChange()"
             placeholder="Type to filter..."
             data-testid="search-input">
      <mat-icon matSuffix>search</mat-icon>
    </mat-form-field>

    @if (filteredCategories.length === 0) {
      <div class="no-results" data-testid="no-results-message">
        <mat-icon>search_off</mat-icon>
        <p>No categories match your search</p>
      </div>
    } @else {
      <mat-accordion class="categories-accordion" multi data-testid="categories-accordion">
        @for (category of filteredCategories; track category.Id) {
          <mat-expansion-panel data-testid="category-panel">
            <mat-expansion-panel-header data-testid="category-header">
              <mat-panel-title>
                <span class="category-name" data-testid="category-name">{{ category.Name }}</span>
                <mat-chip class="error-count-chip" data-testid="category-error-count">{{ category.ErrorTypes.length }} types</mat-chip>
              </mat-panel-title>
              <mat-panel-description data-testid="category-description">
                {{ category.Description }}
              </mat-panel-description>
            </mat-expansion-panel-header>

            <div class="category-content" data-testid="category-content">
              <div class="translations-section" data-testid="translations-section">
                <div class="section-header">
                  <h4>
                    <mat-icon>translate</mat-icon>
                    User-Facing Messages
                  </h4>
                  <button mat-icon-button
                          *hasPermission="permissionTypes.ModifyErrorMessages"
                          [disabled]="!canAddTranslation(category)"
                          (click)="openAddTranslationDialog(category)"
                          matTooltip="Add Translation"
                          data-testid="add-translation-button">
                    <mat-icon>add</mat-icon>
                  </button>
                </div>
                <div class="translations-list" data-testid="translations-list">
                  @for (lang of getTranslationLanguages(category); track lang) {
                    <div class="translation-item" data-testid="translation-item">
                      <span class="language-code" data-testid="translation-language">{{ lang.toUpperCase() }}</span>
                      <span class="translation-message" data-testid="translation-message">{{ category.Translations[lang] }}</span>
                      <button mat-icon-button
                              *hasPermission="permissionTypes.ModifyErrorMessages"
                              (click)="openEditTranslationDialog(category, lang)"
                              matTooltip="Edit Translation"
                              [attr.data-testid]="'edit-translation-button-' + lang">
                        <mat-icon>edit</mat-icon>
                      </button>
                    </div>
                  }
                  @if (getTranslationLanguages(category).length === 0) {
                    <div class="no-translations" data-testid="no-translations">No translations defined</div>
                  }
                </div>
              </div>

              <div class="error-types-section" data-testid="error-types-section">
                <h4>
                  <mat-icon>error_outline</mat-icon>
                  Error Types ({{ category.ErrorTypes.length }})
                </h4>
                @if (category.ErrorTypes.length > 0) {
                  <table mat-table [dataSource]="category.ErrorTypes" class="error-types-table" data-testid="error-types-table">
                    <ng-container matColumnDef="value">
                      <th mat-header-cell *matHeaderCellDef>ID</th>
                      <td mat-cell *matCellDef="let element">
                        <code class="error-id" data-testid="error-type-id">{{ element.Value }}</code>
                      </td>
                    </ng-container>

                    <ng-container matColumnDef="name">
                      <th mat-header-cell *matHeaderCellDef>Name</th>
                      <td mat-cell *matCellDef="let element">
                        <code class="error-name" data-testid="error-type-name">{{ element.Name }}</code>
                      </td>
                    </ng-container>

                    <ng-container matColumnDef="message">
                      <th mat-header-cell *matHeaderCellDef>Admin Description</th>
                      <td mat-cell *matCellDef="let element" data-testid="error-type-message">{{ element.Message }}</td>
                    </ng-container>

                    <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
                    <tr mat-row *matRowDef="let row; columns: displayedColumns;" data-testid="error-type-row"></tr>
                  </table>
                } @else {
                  <div class="no-error-types" data-testid="no-error-types">No error types assigned to this category</div>
                }
              </div>
            </div>
          </mat-expansion-panel>
        }
      </mat-accordion>
    }
  }
</c-container>
